// HTML for Holiday Club invoice and receipt PDFs, and the email bodies that go with them.
//
// The layout follows the Google Docs templates the Apps Script generator used (invoice
// 1kWRZkEi5dgtwYCZ513hY-CAQKY49_HI3cyFNj2lWnZY, receipt 15KNZjetIvSn97wtmUGHBNlqg3tyPjy7YV_HUr-uaz0k).

import { BEE_LOGO_DATA_URI } from "./beeLogo";
import { formatPounds, type HolidayDay, type InvoiceContent } from "./holidayInvoicing";

export const COMPANY = {
  name: "Bee Happy Club LTD",
  address: "19 Courtlands, Maidenhead, SL6 2PT",
  website: "https://bee-happy.club",
  email: "info@bee-happy.club",
  phone: "07849772108",
  accountHolder: "Bee Happy Club Ltd",
  accountNumber: "13156987",
  sortCode: "60-83-71",
  terms: "https://bee-happy.club/contract-with-parents.html",
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const escapeHtml = (text: string) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** "2026-10-16" -> "16 Oct 2026" */
export const formatDate = (isoDate: string) => {
  const [year, month, day] = isoDate.slice(0, 10).split("-").map((part) => parseInt(part, 10));
  return `${day} ${MONTHS[month - 1]} ${year}`;
};

/** Today's date in London as YYYY-MM-DD. */
export const todayInLondon = (now = new Date()) =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/London" }).format(now);

export const STYLE = `
  @page { size: A4; margin: 18mm 20mm; }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: Roboto, Arial, sans-serif; color: #434343; font-size: 11pt; background: #fff; }
  .top { display: flex; justify-content: space-between; align-items: flex-start; }
  .company h1 { font-size: 22pt; font-weight: 400; margin: 0 0 8px; }
  .company div { margin: 2px 0; }
  .company a { color: #1155cc; }
  .title { text-align: right; }
  .title h2 { font-size: 22pt; margin: 0 0 6px; color: #666; letter-spacing: 0.5px; }
  .title img { width: 110px; }
  .parties { display: flex; justify-content: space-between; margin-top: 50px; }
  .customer div:first-child { border-bottom: 1px solid #ccc; padding-bottom: 4px; margin-bottom: 6px; min-width: 170px; }
  .meta td { padding: 2px 4px; font-size: 9.5pt; }
  .meta td:first-child { font-weight: bold; text-align: right; color: #000; }
  table.lines { width: 100%; border-collapse: collapse; margin-top: 50px; }
  table.lines th { font-size: 9.5pt; color: #595959; border: 1px solid #999; padding: 5px; }
  table.lines td { border: 1px solid #ccc; padding: 5px; color: #000; }
  table.lines tr:nth-child(even) td { background: #f3f3f3; }
  .summary { display: flex; justify-content: space-between; margin-top: 6px; }
  .thanks { padding: 12px; color: #000; white-space: pre-line; }
  .totals td { padding: 6px 4px; text-align: right; }
  .totals td:first-child { font-size: 8.5pt; font-weight: bold; color: #44546a; padding-right: 10px; }
  .totals td:last-child { min-width: 120px; border-bottom: 1px solid #ccc; color: #000; }
  .totals tr.due td { font-size: 12pt; font-weight: bold; color: #44546a; }
  .totals tr.due td:last-child { color: #000; border-bottom: 2px solid #000; }
  .terms { margin-top: 30px; font-size: 9.5pt; }
  .terms h3 { font-size: 10pt; color: #000; margin: 0 0 4px; border-bottom: 1px solid #999; display: inline-block; padding-right: 30px; }
  .terms p { margin: 2px 0; }
  .terms a { color: #1155cc; }
`;

const linesTable = (invoice: InvoiceContent) => `
  <table class="lines">
    <thead><tr><th style="width:35%">DESCRIPTION</th><th style="width:20%">QTY</th><th>UNIT PRICE</th><th>TOTAL</th></tr></thead>
    <tbody>
      <tr><td>Full Day</td><td>${invoice.fullDays}</td><td>£ ${formatPounds(invoice.fullDayPricePence)}</td><td>£${formatPounds(invoice.fullDaySubtotalPence)}</td></tr>
      <tr><td>Half Day</td><td>${invoice.halfDays}</td><td>£ ${formatPounds(invoice.halfDayPricePence)}</td><td>£${formatPounds(invoice.halfDaySubtotalPence)}</td></tr>
      <tr><td>&nbsp;</td><td></td><td></td><td></td></tr>
      <tr><td>Discount: ${escapeHtml(invoice.discountReason)}</td><td></td><td>${invoice.discountPercentage}%</td><td>£${formatPounds(invoice.discountPence)}</td></tr>
    </tbody>
  </table>`;

const document = (params: {
  title: string;
  invoice: InvoiceContent;
  meta: [string, string][];
  thanks: string;
  totalLabel: string;
  footer: string;
}) => `<!doctype html>
<html lang="en">
<head><meta charset="utf-8"><title>${escapeHtml(params.title)}</title><style>${STYLE}</style></head>
<body>
  <div class="top">
    <div class="company">
      <h1>${COMPANY.name}</h1>
      <div>${COMPANY.address}</div>
      <div><a href="${COMPANY.website}">${COMPANY.website}</a></div>
      <div>${COMPANY.email}</div>
      <div>${COMPANY.phone}</div>
    </div>
    <div class="title"><h2>${escapeHtml(params.title)}</h2><img src="${BEE_LOGO_DATA_URI}" alt=""></div>
  </div>
  <div class="parties">
    <div class="customer">
      <div>${escapeHtml(params.invoice.parentName)}</div>
      <div>${escapeHtml(params.invoice.address).replace(/\n/g, "<br>")}</div>
    </div>
    <table class="meta">
      ${params.meta.map(([label, value]) => `<tr><td>${label}</td><td>${escapeHtml(value)}</td></tr>`).join("")}
    </table>
  </div>
  ${linesTable(params.invoice)}
  <div class="summary">
    <div class="thanks">${params.thanks}</div>
    <table class="totals">
      <tr><td>SUBTOTAL</td><td>£${formatPounds(params.invoice.subtotalPence)}</td></tr>
      <tr><td>DISCOUNT</td><td>£${formatPounds(params.invoice.discountPence)}</td></tr>
      <tr class="due"><td>${params.totalLabel}</td><td>£${formatPounds(params.invoice.totalPence)}</td></tr>
    </table>
  </div>
  <div class="terms">${params.footer}</div>
</body>
</html>`;

const termsLink = `<p>Terms &amp; Conditions: <a href="${COMPANY.terms}">${COMPANY.terms}</a></p>`;

export const invoiceNumberLabel = (invoiceNo: number) => `#${invoiceNo}`;
export const receiptNumberLabel = (invoiceNo: number) => `#R${invoiceNo}`;

export const renderInvoiceHtml = (invoice: InvoiceContent, invoiceNo: number, issuedDate: string) =>
  document({
    title: "INVOICE",
    invoice,
    meta: [
      ["Invoice No:", invoiceNumberLabel(invoiceNo)],
      ["Invoice Date:", formatDate(issuedDate)],
      ["Due Date:", formatDate(issuedDate)],
    ],
    thanks: "Thank you for your business!",
    totalLabel: "Balance Due",
    footer: `
      <h3>Terms &amp; Instructions</h3>
      <p>Account Holder Name: ${COMPANY.accountHolder}</p>
      <p>Account Number: ${COMPANY.accountNumber}</p>
      <p>Sort Code: ${COMPANY.sortCode}</p>
      <p>&nbsp;</p>
      <p>Use invoice number as the reference.</p>
      <p>&nbsp;</p>
      ${termsLink}`,
  });

export const renderReceiptHtml = (invoice: InvoiceContent, invoiceNo: number, paymentDate: string, receiptDate: string) =>
  document({
    title: "Receipt",
    invoice,
    meta: [
      ["Receipt No:", receiptNumberLabel(invoiceNo)],
      ["Payment Date:", formatDate(paymentDate)],
      ["Receipt Date:", formatDate(receiptDate)],
    ],
    thanks: "Thank you for your payment\nIf you have any questions about this receipt, please contact us.",
    totalLabel: "Amount Paid",
    footer: `<p><b>Payment method:</b> Bank Transfer</p><p>&nbsp;</p>${termsLink}`,
  });

const dayLine = (day: HolidayDay) => {
  if (day.choice === "FULL") return `${escapeHtml(day.label)} - Full day`;
  if (day.choice === "HALF") return `${escapeHtml(day.label)} - Half day`;
  return null;
};

const signOff = `<br>
If you have any questions, feel free to reach out. We are always here to help.<br>
<br>
<br>
Best,<br>
Olesia P.<br>
Bee Happy Club`;

export const INVOICE_EMAIL_SUBJECT = "Bee Happy Club confirmation and invoice";
export const RECEIPT_EMAIL_SUBJECT = "Bee Happy Club receipt";

/** Same wording as the Apps Script generator's TemplatingService.renderInvoiceEmail. */
export const renderInvoiceEmail = (invoice: InvoiceContent) => {
  const days = invoice.children
    .map((child) => `${escapeHtml(child.name)}: <br>${child.days.map(dayLine).filter(Boolean).join("<br>")}`)
    .join("<br><br>");
  return `Hi ${escapeHtml(invoice.parentName)},<br>
<br>
Thank you for choosing Bee Happy Club. We are happy to confirm your booking for the following days:<br>
<br>${days}<br>
<br>
You will find your invoice attached to this email.<br>${signOff}`;
};

export const renderReceiptEmail = (invoice: InvoiceContent) => `Hi ${escapeHtml(invoice.parentName)},<br>
<br>
Thank you for choosing Bee Happy Club. We are happy to confirm your payment has been received.<br>
<br>
You will find your receipt attached to this email.<br>${signOff}`;

/** File names as the Apps Script generator used them in Drive. */
export const invoiceFileName = (invoiceNo: number, parentName: string) => `invoice#${invoiceNo} ${parentName}.pdf`;
export const receiptFileName = (invoiceNo: number, parentName: string) => `receipt#R${invoiceNo} ${parentName}.pdf`;
